/**
 * Data models for the Case Appeals table and `pharmacy/appeal/list` API.
 */

import { CaseAppealsTableColumnKeys } from "../enums/tableColumnKeys";
import {
  TableCellAddKeyButton,
  TableCellMultiline,
  TableCellWithBadge,
} from "./tableCells";

/** Single row returned by backend `pharmacy/appeal/list`. */
export interface CaseAppealListRow {
  identifier: string;
  appeal_id?: string | null;
  appeal_type?: string | null;
  appeal_status?: string | null;
  appeal_poc?: string | null;
  appeal_reason?: string | null;
  appeal_comment?: string | null;
  appeal_probability?: number | null;
  appeal_band?: string | null;
  appeal_sent_at?: string | null;
  appeal_updated_at?: string | null;
  /** Payer case number captured on the appeal outreach, shown as Case ID. */
  appeal_case_id?: string | null;
  /** `YYYY-MM-DD` follow-up date captured on the appeal outreach. */
  appeal_follow_up_date?: string | null;
  patient_name?: string | null;
  patient_mrn?: string | null;
  dob?: string | null;
  drug?: string | null;
  response_status?: string | null;
  filename?: string | null;
  dumped_at?: string | null;
  covermymed_id?: string | null;
  second_stp_status?: string | null;
  /** Denial summary joined from the PA row, shown as the modal's appeal summary. */
  pa_outcome_provider_summary?: string | null;
}

/** Pagination block inside `CaseAppealListApiResponse.details`. */
export interface CaseAppealListPagination {
  total: number;
  page: number;
  limit: number;
}

/** `details` object from backend `pharmacy/appeal/list`. */
export interface CaseAppealListDetails extends CaseAppealListPagination {
  rows: CaseAppealListRow[];
}

/** Raw API response from backend `pharmacy/appeal/list`. */
export interface CaseAppealListApiResponse {
  success?: boolean;
  message?: string;
  details?: CaseAppealListDetails;
}

/** Single predicate condition node for `pharmacy/appeal/list` structured filters. */
export type AppealFilterCondition = {
  type: "condition";
  field: string;
  operator: string;
  value?: string | string[];
};

/** AND/OR group node for `pharmacy/appeal/list` structured filters. */
export type AppealFilterGroup = {
  type: "group";
  operator: "and" | "or";
  children: (AppealFilterCondition | AppealFilterGroup)[];
};

/** POST body for backend `pharmacy/appeal/list`. */
export interface FetchCaseAppealListParams {
  page?: number;
  limit?: number;
  /** Legacy column->values map, or a predicate tree when searching. */
  filters?: Record<string, string[]> | AppealFilterGroup;
  /** Sent as false with a search term so matching is case-insensitive. */
  case_sensitive?: boolean;
  org_id?: string;
}

/**
 * Normalized paginated result used by Case Appeals table hooks after fetch.
 * Mirrors the shape expected by pagination helpers while keeping typed API rows.
 */
export interface CaseAppealListPaginatedResult {
  success: boolean;
  message: string;
  rows: CaseAppealListRow[];
  row_count: number;
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

/**
 * Formatted row passed to `CustomTable` for the Case Appeals page.
 * Includes display columns plus raw API fields for modals and actions.
 */
export interface CaseAppealsTableRowData {
  identifier: string;
  appeal_id: string | null;
  patient_name: string | null;
  patient_mrn: string | null;
  dob: string | null;
  drug: string | null;
  covermymed_id: string | null;
  second_stp_status: string | null;
  response_status: string | null;
  appeal_type: string | null;
  appeal_status: string | null;
  appeal_reason: string | null;
  appeal_comment: string | null;
  appeal_poc: string | null;
  appeal_probability: number | null;
  appeal_band: string | null;
  appeal_sent_at: string | null;
  appeal_updated_at: string | null;
  appeal_case_id: string | null;
  appeal_follow_up_date: string | null;
  filename: string | null;
  dumped_at: string | null;
  pa_outcome_provider_summary: string | null;
  [CaseAppealsTableColumnKeys.DATE_OF_WORK]: TableCellMultiline;
  [CaseAppealsTableColumnKeys.PATIENT_NAME]: TableCellMultiline;
  [CaseAppealsTableColumnKeys.DOB]: string;
  [CaseAppealsTableColumnKeys.DRUG]: string;
  [CaseAppealsTableColumnKeys.COVER_MY_MEDS_ID]: string;
  [CaseAppealsTableColumnKeys.SECOND_STP_STATUS]: TableCellWithBadge;
  [CaseAppealsTableColumnKeys.RESPONSE_STATUS]: TableCellWithBadge;
  [CaseAppealsTableColumnKeys.CASE_ID]: string;
  [CaseAppealsTableColumnKeys.POC]: string;
  [CaseAppealsTableColumnKeys.CASE_STATUS]: TableCellWithBadge;
  [CaseAppealsTableColumnKeys.APPEAL_PROBABILITY]: TableCellWithBadge;
  [CaseAppealsTableColumnKeys.ACTION]: TableCellAddKeyButton;
  [CaseAppealsTableColumnKeys.NEXT_FOLLOW_UP]: string;
}

/**
 * Maps a raw appeal list API row into the Case Appeals table row model.
 *
 * @param row - Row from `pharmacy/appeal/list`
 * @returns Typed table row preserving all API fields
 */
export const mapCaseAppealListRow = (
  row: CaseAppealListRow,
): CaseAppealListRow => ({
  identifier: row.identifier ?? "",
  appeal_id: row.appeal_id ?? null,
  appeal_type: row.appeal_type ?? null,
  appeal_status: row.appeal_status ?? null,
  appeal_poc: row.appeal_poc ?? null,
  appeal_reason: row.appeal_reason ?? null,
  appeal_comment: row.appeal_comment ?? null,
  appeal_probability: row.appeal_probability ?? null,
  appeal_band: row.appeal_band ?? null,
  appeal_sent_at: row.appeal_sent_at ?? null,
  appeal_updated_at: row.appeal_updated_at ?? null,
  appeal_case_id: row.appeal_case_id ?? null,
  appeal_follow_up_date: row.appeal_follow_up_date ?? null,
  patient_name: row.patient_name ?? null,
  patient_mrn: row.patient_mrn ?? null,
  dob: row.dob ?? null,
  drug: row.drug ?? null,
  response_status: row.response_status ?? null,
  filename: row.filename ?? null,
  dumped_at: row.dumped_at ?? null,
  covermymed_id: row.covermymed_id ?? null,
  second_stp_status: row.second_stp_status ?? null,
  pa_outcome_provider_summary: row.pa_outcome_provider_summary ?? null,
});
